import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import api, { API_BASE } from '../lib/api';
import { applyTheme, getTheme } from '../lib/theme';

const syncTheme = (u) => { if (u?.theme && u.theme !== getTheme()) applyTheme(u.theme); };

const AuthContext = createContext(null);

const LS_USER = 'nuyed_auth_user';
const LS_TOKEN = 'nuyed_token';

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try {
      const raw = localStorage.getItem(LS_USER);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });
  const [authError, setAuthError] = useState('');
  const [authLoading, setAuthLoading] = useState(false);
  const [authModal, setAuthModal] = useState({ open: false, mode: 'register' });

  const persist = useCallback((token, u) => {
    try {
      localStorage.setItem(LS_TOKEN, token);
      localStorage.setItem(LS_USER, JSON.stringify(u));
    } catch {}
    setUser(u);
    syncTheme(u);
  }, []);

  // Refresh user from token on mount (+ OAuth redirect handling)
  useEffect(() => {
    const ref = new URLSearchParams(window.location.search).get('ref');
    if (ref) {
      try { localStorage.setItem('nuyed_ref', ref); } catch {}
      api.post('/affiliate/click', { code: ref }).catch(() => {});
    }
    const hash = window.location.hash || '';
    if (hash.includes('oauth_token=')) {
      const token = new URLSearchParams(hash.slice(1)).get('oauth_token');
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
      if (token) {
        try { localStorage.setItem(LS_TOKEN, token); } catch {}
        api.get('/auth/me').then((r) => {
          setUser(r.data);
          syncTheme(r.data);
          try { localStorage.setItem(LS_USER, JSON.stringify(r.data)); } catch {}
        }).catch(() => {});
        return;
      }
    } else if (hash.includes('oauth_error=')) {
      const err = new URLSearchParams(hash.slice(1)).get('oauth_error');
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
      setAuthError(decodeURIComponent(err || 'Social login failed'));
      setAuthModal({ open: true, mode: 'login' });
    }
    const token = (() => { try { return localStorage.getItem(LS_TOKEN); } catch { return null; } })();
    if (token) {
      api.get('/auth/me').then((r) => {
        setUser(r.data);
        syncTheme(r.data);
        try { localStorage.setItem(LS_USER, JSON.stringify(r.data)); } catch {}
      }).catch(() => {
        try { localStorage.removeItem(LS_TOKEN); localStorage.removeItem(LS_USER); } catch {}
        setUser(null);
      });
    }
  }, []);

  const login = useCallback(async (identifier, password, code) => {
    setAuthError(''); setAuthLoading(true);
    try {
      const { data } = await api.post('/auth/login', { identifier, password, code: code || undefined });
      if (data.need_2fa) return 'need_2fa';
      persist(data.token, data.user);
      setAuthModal({ open: false, mode: 'login' });
      return true;
    } catch (e) {
      setAuthError(e?.response?.data?.detail || 'Invalid credentials');
      return false;
    } finally {
      setAuthLoading(false);
    }
  }, [persist]);

  const register = useCallback(async (username, email, password) => {
    setAuthError(''); setAuthLoading(true);
    try {
      let ref = null;
      try { ref = localStorage.getItem('nuyed_ref'); } catch {}
      const { data } = await api.post('/auth/register', { username, email, password, ref: ref || undefined });
      persist(data.token, data.user);
      setAuthModal({ open: false, mode: 'register' });
      return true;
    } catch (e) {
      setAuthError(e?.response?.data?.detail || 'Registration failed');
      return false;
    } finally {
      setAuthLoading(false);
    }
  }, [persist]);

  const socialLogin = useCallback(async (provider) => {
    setAuthError(''); setAuthLoading(true);
    try {
      const { data: providers } = await api.get('/auth/providers');
      if (providers?.[provider]) {
        window.location.href = `${API_BASE}/auth/${provider}/login?origin=${encodeURIComponent(window.location.origin)}`;
        return true;
      }
      setAuthError(`${provider === 'twitter' ? 'X' : provider.charAt(0).toUpperCase() + provider.slice(1)} login is not configured yet — the admin must add the API keys in Admin → Settings`);
      return false;
    } catch (e) {
      setAuthError('Social login failed');
      return false;
    } finally {
      setAuthLoading(false);
    }
  }, [persist]);

  const logout = useCallback(() => {
    try { localStorage.removeItem(LS_TOKEN); localStorage.removeItem(LS_USER); } catch {}
    setUser(null);
  }, []);

  const openAuth = useCallback((mode = 'register') => { setAuthError(''); setAuthModal({ open: true, mode }); }, []);
  const closeAuth = useCallback(() => setAuthModal((s) => ({ ...s, open: false })), []);

  const refreshUser = useCallback(async () => {
    try {
      const { data } = await api.get('/auth/me');
      setUser(data);
      try { localStorage.setItem(LS_USER, JSON.stringify(data)); } catch {}
    } catch {}
  }, []);

  return (
    <AuthContext.Provider
      value={{ user, isAuthed: !!user, login, register, socialLogin, logout, refreshUser,
               authModal, openAuth, closeAuth, authError, authLoading }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
